import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Check } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, user, login } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleStep1Submit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.phone || !formData.email || !formData.password) {
      setError('Por favor preencha todos os campos');
      return;
    }

    if (formData.password.length < 6) {
      setError('A password deve ter pelo menos 6 caracteres');
      return;
    }

    setStep(2);
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) return;

    setError('');
    setIsLoading(true);

    const { error } = await register(
      formData.email,
      formData.password,
      formData.name,
      'Contabilistas',
      formData.phone
    );

    if (error) {
      setError(error);
      setIsLoading(false);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'userSignedUp',
        userEmail: formData.email,
        userArea: 'Contabilistas',
        userName: formData.name,
        timestamp: new Date().toISOString()
      });

      const loginResult = await login(formData.email, formData.password);
      if (loginResult.error) {
        alert('Conta criada! Por favor faça login.');
        navigate('/login');
      } else {
        navigate('/dashboard');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md border border-primary/10">
        <div className="flex items-center justify-center mb-8">
          <img src="/fiscobem.svg" alt="fiscobem" className="h-7" />
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Passo {step} de 2</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-primary h-2 rounded-full transition-all duration-300"
              style={{ width: step === 1 ? '50%' : '100%' }}
            ></div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={handleStep1Submit} className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Criar Conta</h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="João Silva"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telefone</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="912 345 678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="seu@email.pt"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Mínimo 6 caracteres"
              />
            </div>

            <button
              type="submit"
              className="w-full py-3 bg-gradient-primary text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              Continuar
            </button>
          </form>
        ) : (
          <form onSubmit={handleStep2Submit} className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirmar e Aceitar</h2>
              <p className="text-gray-600">Reveja o preço por pedido de contacto e aceite os termos e condições</p>
            </div>

            {/* Pricing box */}
            <div className="bg-secondary/10 border-2 border-secondary/30 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-700 font-medium">Contabilidade</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-primary">5€</span>
                  <span className="text-gray-600">/lead</span>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Pague apenas pelos pedidos de contacto que desbloquear</span>
              </div>
            </div>

            {/* Terms & Conditions checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-0.5 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  acceptedTerms
                    ? 'bg-primary border-primary'
                    : 'border-gray-300 group-hover:border-primary'
                }`}>
                  {acceptedTerms && <Check className="w-3 h-3 text-white" />}
                </div>
              </div>
              <span className="text-sm text-gray-700 leading-relaxed">
                Li e aceito os{' '}
                <a
                  href="https://fiscobem.pt/termos"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  Termos e Condições
                </a>
              </span>
            </label>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={!acceptedTerms || isLoading}
                className="w-full py-3 bg-gradient-primary text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'A criar conta...' : 'Criar Conta'}
              </button>
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={isLoading}
                className="w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Voltar
              </button>
            </div>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-gray-600">
          Já tem conta?{' '}
          <button onClick={() => navigate('/login')} className="text-primary hover:underline font-medium">
            Entrar
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
